(ns frontend.view.modals.entity-esign.view
  (:require
   [common.localization :as loc]
   [frontend.view.components.upload :as upload]
   [frontend.view.modals.components.processing-modal :as processing-modal]
   [frontend.view.modals.entity-esign.events :as event]
   [frontend.view.modals.entity-esign.sub :as sub]
   [re-frame.core :as rf]
   [ui.color :as c]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.editors.labelled-input.view :as labelled-input]
   [frontend.state.current-session :as session]))

(defonce modal-key :entity-esign-modal)

(defn view-body
  [{:keys [state file-uploaded? signatories]}]
  (let [file @(rf/subscribe [::sub/file])]
    [:div
     {:class "flex flex-col gap-2 items-start"}
     (when-not file-uploaded?
       [:<>
        [:p
         {:class "font-bold mb-2"}
         (loc/tr :esign-upload-pdf-info-text)]
        [upload/custom-upload-zone
         {:css-class      "attachments-module-upload-zone"
          :lang-kw        :en
          :accept         ".pdf"
          :on-change!     (fn [{:keys [files original-event]}] (rf/dispatch [::event/set-file (aget files 0)]))
          :pre-link-text  :custom-dropzone-pre-link-esign
          :link-text      :custom-dropzone-link-esign
          :post-link-text :custom-dropzone-post-link-esign}]])
     (when file-uploaded?
       [:<>
        [:p
         {:class "font-bold mb-2"}
         (loc/tr :esign-file-to-sign)]
        [:span {:class "flex gap-3"}
         (if (nil? file) (loc/tr :no-file-chosen) (.-name file))
         [button/square-secondary-open
          {:size       :extra-small
           :tooltip    (loc/tr :remove-file)
           :aria-label (loc/tr :remove-file)
           :on-click   #(rf/dispatch [::event/remove-file])}
          [:i.fas.fa-trash]]]
        [:div
         {:class "w-full py-3 flex flex-col gap-3 border-t border-solid border-base-light-border-paler"}
         [:p
          {:class "font-bold mb-2"}
          (loc/tr :esign-signatories)]
         [:div {:class "w-full mb-0"}
          [button/sm-primary-solid
           {:on-click (fn [] (rf/dispatch [::event/add-signatory]))}
           (str "+ " (loc/tr :add-signatory))]]
         (->> signatories
              (map-indexed
               (fn [i signatory]
                 ^{:key i}
                 [:div
                  {:class "p-2 *:w-full grid md:grid-cols-10 gap-3 items-baseline bg-base-regular rounded"}
                  [:div
                   {:class "col-span-5"}
                   [labelled-input/view
                    {:label     (loc/tr :signatory/name)
                     :data-type :data.type/text-simple
                     :value     (:name signatory)
                     :on-change (fn [new-value]
                                  (rf/dispatch [::event/set-signatory-field i :name new-value]))}]

                   [:div
                    {:class "mb-2 flex flex-col w-full"}
                    [:span {:class "text-danger-light-text text-xs"} (get-in signatory [:errors :name])]]]

                  [:div
                   {:class "col-span-4"}
                   [labelled-input/view
                    {:label     (loc/tr :signatory/email)
                     :data-type :data.type/text-simple
                     :value     (:email signatory)
                     :on-change (fn [new-value]
                                  (rf/dispatch [::event/set-signatory-field i :email new-value]))}]

                   [:div
                    {:class "flex flex-col w-full"}
                    [:span
                     {:class "text-danger-light-text text-xs"}
                     (get-in signatory [:errors :email])]]]

                  [:div
                   {:class "col-span-1 flex justify-end"}
                   [button/square-secondary-hollow
                    {:tooltip    (loc/tr :entity-esign/remove-signatory)
                     :aria-label (loc/tr :entity-esign/remove-signatory)
                     :on-click   (fn [] (rf/dispatch [::event/remove-signatory i]))}
                    [:i.fas.fa-trash-alt]]]])))]])]))

(defn footer-view
  [{:keys [state valid-data? duplicate-emails? file-uploaded? file-sent? entity-id on-close! signatories]}]
  (let [disabled? (not (and valid-data? file-uploaded? (not duplicate-emails?)))
        signatory-count (count signatories)
        esign-v2? @session/tenant-esign-v2?]
    [:<>
     [modal/left-cancel-only
      (fn [_]
        (rf/dispatch [::event/clear-modal-data])
        (on-close!))
      nil]
     (when file-uploaded?
       [tooltip/view
        {:disabled? (not duplicate-emails?)}
        [:div
         [modal/right-single-button
          nil
          disabled?
          (fn []
            (processing-modal/start-processing! state)
            (if esign-v2?
              (rf/dispatch [::event/v2-create {:entity-id entity-id}])
              (rf/dispatch [::event/create-esign-process entity-id #(processing-modal/error! state %)])))
          (str "Initiate signing for " signatory-count (if (> signatory-count 1) " signatories" " signatory"))
          nil]]
        [:span (loc/tr :esign-email-exists)]])]))

(defn view
  [{:keys [shown? close-modal! props state class-name topmost?]}]
  (fn [{:keys [shown? close-modal! props class-name topmost?]}]
    (let [{:keys [oppo-id]} props
          valid-data? @(rf/subscribe [::sub/valid-signatory-data?])
          file-uploaded? @(rf/subscribe [::sub/esign-attachment-uploaded?])
          file-sent? @(rf/subscribe [::sub/sent-successfully?])
          signatories @(rf/subscribe [::sub/signatories])
          duplicate-emails? @(rf/subscribe [::sub/duplicate-emails?])]
      [processing-modal/render-modal
       {:state             state
        :shown?            (and shown? topmost?)
        :success-text      (loc/tr :success-guest-invited)
        :on-close!         (fn []
                             (rf/dispatch [::event/clear-modal-data])
                             (close-modal!))
        :class-name        "overflow-initial"
        :header-color      ::c/bg-2
        :default-title-fn  (constantly (loc/tr :esign))
        :default-body-fn   (partial view-body
                                    {:state          state
                                     :file-uploaded? file-uploaded?
                                     :signatories    signatories})
        :default-footer-fn (partial footer-view
                                    {:state             state
                                     :valid-data?       valid-data?
                                     :duplicate-emails? duplicate-emails?
                                     :file-uploaded?    file-uploaded?
                                     :file-sent?        file-sent?
                                     :entity-id         oppo-id
                                     :on-close!         close-modal!
                                     :signatories       signatories})}])))
